import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { tinykeys } from "tinykeys";

import { SHORTCUTS, isInputFocused } from "@/lib/shortcuts";

interface NavigationShortcutsOptions {
  /** Open the command palette. */
  onOpenCommand: () => void;
  /** Open the shortcuts help panel. */
  onOpenShortcutsHelp: () => void;
  /** Open the global search (defaults to opening command palette). */
  onOpenSearch?: () => void;
}

/**
 * Wires the global keyboard shortcut layer for the v2 app:
 *  - `g X` two-key navigation sequences (no modifier, suppressed when typing)
 *  - `⌘K` / `Ctrl+K` opens the command palette (always active)
 *  - `?` opens the shortcuts help panel (suppressed when typing)
 *  - `/` focuses the search/command palette (suppressed when typing)
 *
 * The `g X` sequences come from the SHORTCUTS registry's `navigation` group so
 * the registry stays the single source of truth — adding a new G-shortcut only
 * requires a new registry entry + a route.
 */
export function useNavigationShortcuts({
  onOpenCommand,
  onOpenShortcutsHelp,
  onOpenSearch,
}: NavigationShortcutsOptions) {
  const navigate = useNavigate();

  useEffect(() => {
    // Build the binding map from the registry. Suppress single-key shortcuts
    // when the user is typing into an input.
    const navHandlers: Record<string, (e: KeyboardEvent) => void> = {};

    for (const s of SHORTCUTS) {
      if (!s.href) continue;
      navHandlers[s.keys] = (e: KeyboardEvent) => {
        if (isInputFocused(e.target)) return;
        e.preventDefault();
        navigate(s.href!);
      };
    }

    const unbind = tinykeys(window, {
      ...navHandlers,
      "$mod+KeyK": (e) => {
        e.preventDefault();
        onOpenCommand();
      },
      "Shift+Slash": (e) => {
        if (isInputFocused(e.target)) return;
        e.preventDefault();
        onOpenShortcutsHelp();
      },
      Slash: (e) => {
        if (isInputFocused(e.target)) return;
        e.preventDefault();
        if (onOpenSearch) onOpenSearch();
        else onOpenCommand();
      },
    });

    return () => unbind();
  }, [navigate, onOpenCommand, onOpenShortcutsHelp, onOpenSearch]);
}
